import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import {
  Circle,
  GoogleMap,
  Polygon,
  useJsApiLoader,
} from '@react-google-maps/api';
import { Geofence, GeofenceShapeType } from '@models/geofence.model';
import { DEFAULT_GEOFENCE_COLOR } from '@utils/geofenceCategoryIcons';
import {
  GOOGLE_MAPS_LIBRARIES,
  GOOGLE_MAPS_LOADER_ID,
  GOOGLE_MAPS_VERSION,
} from '@utils/googleMapsConfig';

const DEFAULT_CENTER = { lat: 39.8283, lng: -98.5795 };
const DEFAULT_ZOOM = 4;

interface GeofenceMapProps {
  geofences: Geofence[];
  selectedGeofenceId?: string | null;
  onSelectGeofence?: (geofence: Geofence) => void;
  size?: { width: string; height: string };
}

const buildShapeOptions = (color: string, isSelected: boolean) => ({
  fillColor: color,
  fillOpacity: isSelected ? 0.35 : 0.2,
  strokeColor: color,
  strokeOpacity: isSelected ? 0.95 : 0.7,
  strokeWeight: isSelected ? 3 : 2,
  clickable: true,
  zIndex: isSelected ? 2 : 1,
});

// Extend a LatLngBounds to cover the full geofence shape so fitBounds zooms
// out enough to show the entire area:
//  - POLYGON: every vertex
//  - POINT_RADIUS: 4 corners of the circle's bounding box (computed from
//    centre + radius without needing the Google "geometry" library)
const extendBoundsForGeofence = (
  bounds: google.maps.LatLngBounds,
  geofence: Geofence,
): void => {
  if (
    geofence.shape.type === GeofenceShapeType.POINT_RADIUS &&
    geofence.shape.center &&
    geofence.shape.radiusMeters
  ) {
    const { lat, lng } = geofence.shape.center;
    const r = geofence.shape.radiusMeters;
    const latDelta = r / 111320;
    const lngDelta = r / (111320 * Math.cos((lat * Math.PI) / 180));
    bounds.extend(new window.google.maps.LatLng(lat - latDelta, lng - lngDelta));
    bounds.extend(new window.google.maps.LatLng(lat + latDelta, lng + lngDelta));
  } else if (
    geofence.shape.type === GeofenceShapeType.POLYGON &&
    geofence.shape.polygonCoordinates
  ) {
    geofence.shape.polygonCoordinates.forEach((p) => {
      bounds.extend(new window.google.maps.LatLng(p.lat, p.lng));
    });
  }
};

export const GeofenceMap: React.FC<GeofenceMapProps> = ({
  geofences,
  selectedGeofenceId,
  onSelectGeofence,
  size = { width: '100%', height: '100%' },
}) => {
  const { isLoaded } = useJsApiLoader({
    id: GOOGLE_MAPS_LOADER_ID,
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY || '',
    libraries: GOOGLE_MAPS_LIBRARIES,
    version: GOOGLE_MAPS_VERSION,
  });

  const mapRef = useRef<google.maps.Map | null>(null);

  const visibleGeofences = useMemo(
    () => geofences.filter((g) => g.shape && (g.shape.center || g.shape.polygonCoordinates)),
    [geofences],
  );

  const fitMapToGeofences = useCallback(() => {
    const map = mapRef.current;
    if (!map) return;

    if (visibleGeofences.length === 0) {
      map.setCenter(DEFAULT_CENTER);
      map.setZoom(DEFAULT_ZOOM);
      return;
    }

    const bounds = new window.google.maps.LatLngBounds();
    visibleGeofences.forEach((g) => extendBoundsForGeofence(bounds, g));

    if (!bounds.isEmpty()) {
      map.fitBounds(bounds, { top: 60, bottom: 60, left: 60, right: 60 });
    }
  }, [visibleGeofences]);

  const onLoad = useCallback(
    (mapInstance: google.maps.Map) => {
      mapRef.current = mapInstance;
      fitMapToGeofences();
    },
    [fitMapToGeofences],
  );

  const onUnmount = useCallback(() => {
    mapRef.current = null;
  }, []);

  useEffect(() => {
    fitMapToGeofences();
  }, [fitMapToGeofences]);

  // Pan/zoom is now handled by fitMapToGeofences itself: whenever the visible
  // geofence list changes (e.g., the user picks a different row in the table),
  // fitBounds re-frames the entire selected shape with appropriate padding.

  if (!isLoaded) {
    return <div style={size} className="bg-gray-50 animate-pulse" />;
  }

  return (
    <GoogleMap
      mapContainerStyle={size}
      center={DEFAULT_CENTER}
      zoom={DEFAULT_ZOOM}
      onLoad={onLoad}
      onUnmount={onUnmount}
      options={{
        zoomControl: true,
        mapTypeControl: true,
        streetViewControl: false,
        fullscreenControl: true,
      }}
    >
      {visibleGeofences.map((geofence) => {
        const color =
          geofence.category?.color || geofence.color || DEFAULT_GEOFENCE_COLOR;
        const isSelected = geofence.geofenceId === selectedGeofenceId;
        const opts = buildShapeOptions(color, isSelected);

        if (
          geofence.shape.type === GeofenceShapeType.POINT_RADIUS &&
          geofence.shape.center &&
          geofence.shape.radiusMeters
        ) {
          return (
            <Circle
              key={geofence.geofenceId}
              center={geofence.shape.center}
              radius={geofence.shape.radiusMeters}
              options={opts}
              onClick={() => onSelectGeofence?.(geofence)}
            />
          );
        }

        if (
          geofence.shape.type === GeofenceShapeType.POLYGON &&
          geofence.shape.polygonCoordinates &&
          geofence.shape.polygonCoordinates.length >= 3
        ) {
          return (
            <Polygon
              key={geofence.geofenceId}
              paths={geofence.shape.polygonCoordinates}
              options={opts}
              onClick={() => onSelectGeofence?.(geofence)}
            />
          );
        }

        return null;
      })}
    </GoogleMap>
  );
};

export default GeofenceMap;
